import { useQuery } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import { z } from "zod";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

const assignmentSchema = z.object({
  id: z.string(),
  planId: z.string(),
  title: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  isCancelled: z.boolean(),
  campus: z.string().nullable(),
  campusTimeZone: z.string().nullable(),
  team: z.string(),
  position: z.string(),
  response: z.enum(["no_reply", "accepted", "declined"]),
  respondedAt: z.string().nullable(),
  note: z.string().nullable(),
  season: z.string().nullable(),
  requestedAt: z.string().nullable(),
  message: z.string().nullable(),
  conflict: z.boolean(),
});
const blockoutSchema = z.object({
  id: z.string(),
  startsOn: z.string(),
  endsOn: z.string(),
  reason: z.string().nullable(),
});
const scheduleSchema = z.object({
  linked: z.boolean(),
  assignments: z.array(assignmentSchema),
  blockouts: z.array(blockoutSchema),
});
export type MySchedule = z.infer<typeof scheduleSchema>;
export type MyAssignment = z.infer<typeof assignmentSchema>;
export type MyBlockout = z.infer<typeof blockoutSchema>;

/** This church's dates and blockouts (my_schedule): Home's "waiting for your answer" count reads it (ADR 0070 R2). */
export function useMySchedule() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["my-schedule", tenantId],
    enabled: tenantId !== null,
    retry: false,
    queryFn: async (): Promise<MySchedule> => {
      const { data, error } = await supabase.rpc("my_schedule", { p_tenant_id: tenantId ?? "" });
      if (error) throw error;
      return scheduleSchema.parse(data);
    },
  });
}
